import React, { useState } from "react";
import { auth, provider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { 
  Sprout, 
  ArrowRight, 
  ShieldCheck, 
  Compass, 
  Globe2, 
  Network 
} from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      await signInWithPopup(auth, provider);
      navigate("/");
    } catch (error) {
      console.error("Auth Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040707] text-slate-300 font-sans selection:bg-emerald-500/30 flex items-center justify-center p-0 lg:p-6 overflow-hidden">
      
      {/* --- AMBIENT BIOLUMINESCENCE --- */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[600px] h-[600px] bg-emerald-900/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-indigo-900/20 rounded-full blur-[120px]" />
      </div>

      <main className="relative z-10 w-full max-w-[1400px] min-h-[100vh] lg:min-h-[85vh] bg-[#0A0F0F]/80 backdrop-blur-3xl lg:rounded-[40px] border border-white/5 flex flex-col lg:flex-row overflow-hidden shadow-2xl shadow-black/50">
        
        {/* --- LEFT: PRODUCT VISION --- */}
        <div className="w-full lg:w-1/2 p-10 lg:p-20 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Sprout className="text-black w-6 h-6" />
            </div>
            <div>
              <h2 className="text-white font-bold tracking-tight text-lg leading-none">Any Time Farming</h2>
              <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-500/70 font-bold">Saola Innovations</span>
            </div>
          </div>

          <div className="mt-20 lg:mt-0">
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-[1.05] tracking-tighter mb-8">
              Any Time <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-200 to-indigo-400">
Farming              </span>
            </h1>
            <p className="text-slate-400 text-lg max-w-md leading-relaxed">
              ATF translates complex field data into actionable intelligence. 
              The future of agriculture isn't just smart—it's autonomous.
            </p>
          </div>

          {/* <div className="hidden lg:flex items-center gap-8 mt-12">
            <div className="flex flex-col gap-1">
              <span className="text-white font-bold text-2xl">99.9%</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Node Uptime</span>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="flex flex-col gap-1">
              <span className="text-white font-bold text-2xl">24/7</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Field Monitoring</span>
            </div>
          </div> */}
        </div>

        {/* --- RIGHT: ACCESS PORTAL --- */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-10 lg:p-20 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="w-full max-w-sm">
            <div className="mb-10 text-center lg:text-left">
              <h3 className="text-3xl font-bold text-white mb-2 tracking-tight">Access Dashboard</h3>
              <p className="text-slate-500 text-sm">Secure  Google verification</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={signInWithGoogle}
                disabled={loading}
                className="group relative w-full bg-white text-black py-4 px-6 rounded-2xl font-bold transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] active:scale-[0.98] flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="p-1 bg-slate-100 rounded-lg group-hover:scale-110 transition-transform">
                    <FcGoogle className="text-xl" />
                  </div>
                  <span className="text-sm uppercase tracking-wider">Authorize Session</span>
                </div>
                {loading ? (
                  <div className="h-5 w-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                )}
              </button>

              <div className="grid grid-cols-3 gap-2 py-6">
                <div className="h-1 bg-emerald-500/20 rounded-full" />
                <div className="h-1 bg-emerald-500/50 rounded-full animate-pulse" />
                <div className="h-1 bg-emerald-500/20 rounded-full" />
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3 text-xs text-slate-500 bg-white/5 p-4 rounded-2xl border border-white/5">
                  <ShieldCheck className="w-5 h-5 text-emerald-500" />
                  <span>Encrypted.</span>
                </div>
              </div>
            </div>

            <footer className="mt-20 lg:mt-32">
              <div className="flex justify-between text-[10px] uppercase tracking-widest font-bold text-slate-600">
                <div className="flex gap-4">
                  <a href="#" className="hover:text-emerald-500 transition-colors">Privacy</a>
                  <a href="#" className="hover:text-emerald-500 transition-colors">Terms</a>
                </div>
                <span>© 2025</span>
              </div>
            </footer>
          </div>
        </div>
      </main>
      
      {/* Floating Status Bar (Mobile) */}
      <div className="lg:hidden fixed bottom-6 left-6 right-6 h-12 bg-emerald-500 rounded-2xl flex items-center justify-between px-6 shadow-xl shadow-emerald-500/20">
         <span className="text-black font-bold text-[10px] uppercase tracking-widest">ATF System Status</span>
         <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-black rounded-full animate-ping" />
            <span className="text-black font-bold text-[10px]">Online</span>
         </div>
      </div>
    </div>
  );
};

export default LoginPage;